import {createColumnHelper} from "@tanstack/react-table";
import i18next from "i18next";
import {useTranslation} from "react-i18next";
import {truncateString} from "../../Utils/StringHelper";
import {TextInput} from "../../Components/Form/TextInput";
import {SecondaryButton} from "../../Components/Buttons/SecondaryButton";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faDownload, faRotate, faCommentDots} from "@fortawesome/free-solid-svg-icons";
import {TanStackTable} from "../../Components/Tables/TanStackTable";
import {BaseCard} from "../../Components/Cards/BaseCard";
import {SignerPills} from "../../Components/Cards/SignerPills";
import {useBoundStore} from "../../Store/store";
import {useEffect, useState} from "react";
import {PrimaryButton} from "../../Components/Buttons/PrimaryButton";
import dayjs from "dayjs";
import * as React from "react";
import {DangerButton} from "../../Components/Buttons/DangerButton";
import {ScoreDialog} from "../../Components/Dialogs/PerformanceReports/ScoreDialog";
import {RejectDialog} from "../../Components/Dialogs/PerformanceReports/RejectDialog";
import {RegenerateConfirmDialog} from "../../Components/Dialogs/PerformanceReports/RegenerateConfirmDialog";
import {RemarksDialog} from "../../Components/Dialogs/PerformanceReports/RemarksDialog";
import toast from "react-hot-toast";

export const MyPerformanceReports = () => {
    const {t} = useTranslation();
    const performanceStatements = useBoundStore((state) => state.performanceStatements)
    const performanceStatementsMetaData = useBoundStore((state) => state.performanceStatementsMetaData)
    const getMyPerformanceStatements = useBoundStore((state) => state.getMyPerformanceStatements)
    const regeneratePerformanceStatement = useBoundStore((state) => state.regeneratePerformanceStatement)
    const [statusFilter, setStatusFilter] = useState([]);
    const [forceNewData, setForceNewData] = useState(false);

    const [openScorePopup, setOpenScorePopup] = useState(false);
    const [openRejectReasonPopup, setOpenRejectReasonPopup] = useState(false);
    const [openRegenerateDialog, setOpenRegenerateDialog] = useState(false);
    const [openRemarksDialog, setOpenRemarksDialog] = useState(false);
    const [selectedPerformanceStatement, setSelectedPerformanceStatement] = useState(null);
    const [selectedRemarks, setSelectedRemarks] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchFilter, setSearchFilter] = useState('');
    const [isRegenerating, setIsRegenerating] = useState(false);

    const handleFilterClick = (status) => {
        if(statusFilter.includes(status)) {
            setStatusFilter(statusFilter.filter(s => s !== status))
        } else {
            setStatusFilter([...statusFilter, status])
        }
    }

    useEffect(() => {
        setForceNewData(!forceNewData);
    }, [performanceStatements]);

    useEffect(() => {
        setCurrentPage(1);
    }, [statusFilter, searchFilter]);

    useEffect(() => {
        getMyPerformanceStatements(statusFilter, currentPage, searchFilter)
    }, [getMyPerformanceStatements, statusFilter, currentPage, searchFilter]);

    const handleRegeneratePerformanceStatement = async () => {
        setIsRegenerating(true);
        try {
            await regeneratePerformanceStatement(selectedPerformanceStatement);
            toast.success(t('performance_reports.toast_regenerate_success'));
            setOpenRegenerateDialog(false);
            await getMyPerformanceStatements(statusFilter, currentPage, searchFilter);
        } catch (error) {
            toast.error(t('performance_reports.toast_regenerate_error'));
        } finally {
            setIsRegenerating(false);
        }
    };

    const columnHelper = createColumnHelper()
    const columns = [
        columnHelper.accessor('from.formatted', {
            header: t('performance_reports.column_from'),
            cell: props => dayjs(props.cell.getValue()).format('DD/MM/YYYY'),
        }),
        columnHelper.accessor('branch', {
            header: t('performance_reports.column_branch'),
            cell: props => truncateString(`${props.cell.getValue()?.company_name ?? '-'}`),
        }),
        columnHelper.accessor('freelancer', {
            header: i18next.t('shifts_module.column_freelancer'),
            cell: props => truncateString(`${props.cell.getValue()?.first_name ?? '-'} ${props.cell.getValue()?.last_name ?? ''}`),
        }),
        columnHelper.accessor('status', {
            header: t('performance_reports.column_status'),
            cell: props => {
                return (
                    <div className={'flex flex-row space-x-4'}>
                        {
                            ['accepted'].includes(props.row.original.status) && (
                                <div className={'px-2 py-1 bg-green-300 text-green-900 rounded-full text-xs opacity-70'}>{t('performance_reports.status_accepted')}</div>
                            )
                        }

                        {
                            ['sent'].includes(props.row.original.status) && (
                                <div className={'px-2 py-1 bg-blue-300 text-blue-900 rounded-full text-xs opacity-70'}>{t('performance_reports.status_waiting_for_signers')}</div>
                            )
                        }

                        {
                            ['feedback_required_freelancer'].includes(props.row.original.status) && (
                                <div className={'px-2 py-1 bg-gray-300 text-gray-900 rounded-full text-xs opacity-70'}>{t('performance_reports.status_waiting_for_freelancer')}</div>
                            )
                        }

                        {
                            ['feedback_required_customer'].includes(props.row.original.status) && (
                                <div className={'px-2 py-1 bg-orange-300 text-orange-900 rounded-full text-xs opacity-70'}>{t('performance_reports.status_waiting_for_customer')}</div>
                            )
                        }

                        {
                            ['pending'].includes(props.row.original.status) && (
                                <div className={'px-2 py-1 bg-orange-300 text-orange-900 rounded-full text-xs opacity-70'}>{t('performance_reports.status_pending')}</div>
                            )
                        }

                        {
                            ['rejected'].includes(props.row.original.status) && (
                                <div className={'px-2 py-1 bg-red-300 text-red-900 rounded-full text-xs opacity-70'}>{t('performance_reports.status_rejected')}</div>
                            )
                        }
                    </div>
                )
            },
        }),
        columnHelper.accessor('documenso_data', {
            header: t('performance_reports.column_signers'),
            cell: props => <SignerPills recipients={props.cell.getValue()?.recipients} />,
        }),
        columnHelper.accessor('remarks', {
            header: t('performance_reports.column_remarks'),
            cell: props => {
                const remarks = props.cell.getValue() || [];
                const remarksCount = remarks.length;

                if (remarksCount === 0) {
                    return <p className="text-gray-400">-</p>;
                }

                return (
                    <button
                        onClick={() => {
                            setSelectedRemarks(remarks);
                            setOpenRemarksDialog(true);
                        }}
                        className="relative inline-flex items-center justify-center"
                    >
                        <FontAwesomeIcon
                            icon={faCommentDots}
                            className="text-primary cursor-pointer hover:text-primary-700 transition-colors text-xl"
                        />
                        {remarksCount > 0 && (
                            <span className="absolute -top-2 -right-2 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white transform bg-red-600 rounded-full min-w-[18px]">
                                {remarksCount}
                            </span>
                        )}
                    </button>
                );
            },
        }),
        columnHelper.accessor('document', {
            header: t('performance_reports.column_document'),
            cell: props => {
                if(props.cell.getValue() !== null && props.cell.getValue().hasOwnProperty('url') ) {
                    return (
                        <div className={'flex flex-row space-x-4 items-center'}>
                            <FontAwesomeIcon icon={faDownload} className={'text-primary cursor-pointer'} onClick={() => window.open(props.cell.getValue().url, '_blank')}/>
                            <button
                                onClick={() => {
                                    setSelectedPerformanceStatement(props.row.original.id);
                                    setOpenRegenerateDialog(true);
                                }}
                                className={'text-orange-600 hover:text-orange-800 transition-colors'}
                                title={t('performance_reports.regenerate_title')}
                            >
                                <FontAwesomeIcon icon={faRotate} className={'cursor-pointer'} />
                            </button>
                        </div>
                    )
                } else {
                    return (<p>-</p>)
                }
            },
        }),
        columnHelper.accessor('actions', {
            header: t('performance_reports.column_actions'),
            cell: props => {
                return (
                    <div className={'flex flex-row items-center space-x-4'}>
                        {
                            ['sent', 'pending', 'feedback_required_customer'].includes(props.row.original.status) && (
                                <>
                                    <PrimaryButton
                                        fullWidth={false}
                                        onClick={() => {
                                            setOpenScorePopup(true)
                                            setSelectedPerformanceStatement(props.row.original.id)
                                        }}
                                    >
                                        {t('general.actions.accept')}
                                    </PrimaryButton>
                                    <DangerButton fullWidth={false} onClick={() => {
                                        setOpenRejectReasonPopup(true)
                                        setSelectedPerformanceStatement(props.row.original.id)
                                    }}>
                                        {t('general.actions.reject')}
                                    </DangerButton>
                                </>
                            )
                        }
                    </div>
                )
            },
        })
    ]

    return (
        <div>
            <div className={'mb-2 '}>
                <div className={'flex justify-between'}>
                    <div className={'my-auto'}>
                        <TextInput
                            type={'text'}
                            name={'search'}
                            translationIndex={null}
                            placeholder={t('general.actions.search')}
                            onChange={(e) => setSearchFilter(e.target.value)}
                        >
                        </TextInput>
                    </div>
                    <div className={'grid grid-cols-2 gap-4 my-auto'}>
                        <div>
                            <SecondaryButton outlined={!statusFilter.includes('pending,feedback_required_freelancer,feedback_required_customer')} onClick={() => {
                                handleFilterClick('pending,feedback_required_freelancer,feedback_required_customer')
                            }}>
                                {t('performance_reports.filter_pending')}
                            </SecondaryButton>
                        </div>
                        <div>
                            <SecondaryButton outlined={!statusFilter.includes('accepted')} onClick={() => {
                                handleFilterClick('accepted')
                            }}>
                                {t('performance_reports.filter_accepted')}
                            </SecondaryButton>
                        </div>
                    </div>
                </div>
            </div>
            <BaseCard>
                {performanceStatements?.length === 0 ? (
                    <p data-testid="empty-state" className="text-sm text-gray-500 text-center py-8">{t('performance_reports.no_results')}</p>
                ) : (
                    <TanStackTable
                        columns={columns}
                        tableData={performanceStatements}
                        forceNewData={forceNewData}
                        laravelPaginationMetaData={performanceStatementsMetaData}
                        onChangePage={(page) => {
                            setCurrentPage(page +1)
                        }}
                    />
                )}
            </BaseCard>


            <ScoreDialog
                isOpen={openScorePopup}
                onClose={() => setOpenScorePopup(false)}
                performanceStatementId={selectedPerformanceStatement}
                onSuccess={() => getMyPerformanceStatements(statusFilter, currentPage, searchFilter)}
            />
            <RejectDialog
                isOpen={openRejectReasonPopup}
                onClose={() => setOpenRejectReasonPopup(false)}
                performanceStatementId={selectedPerformanceStatement}
                onSuccess={() => getMyPerformanceStatements(statusFilter, currentPage, searchFilter)}
            />
            <RegenerateConfirmDialog
                isOpen={openRegenerateDialog}
                onClose={() => setOpenRegenerateDialog(false)}
                onConfirm={handleRegeneratePerformanceStatement}
                isLoading={isRegenerating}
            />
            <RemarksDialog
                open={openRemarksDialog}
                onClose={() => setOpenRemarksDialog(false)}
                remarks={selectedRemarks}
            />
        </div>
    )
}